/**
 * You May Also Like - Slider Initialization
 */
(() => {
// The colorway swap re-runs section scripts, so the class must stay function scoped
// and the second run must bail out before redeclaring it.
if (customElements.get('you-may-also-like')) return;

class YouMayAlsoLike extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.baseUrl = this.dataset.url;
    
    // Intersection Observer to load recommendations when visible
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.loadRecommendations();
        this.observer.unobserve(this);
      }
    }, { rootMargin: '0px 0px 400px 0px' });
    
    this.observer.observe(this);

    // Delegate click handling for quick add + card navigation (same behavior as product-collection-showcase)
    this.addEventListener('click', (event) => {
      const addToCartButton = event.target.closest('.product-collection-showcase__card-add-to-cart');
      if (addToCartButton) {
        if (addToCartButton.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const productHandleValue = addToCartButton.dataset.productHandle;
        const productIdValue = addToCartButton.getAttribute('data-product-id');
        const prefetched =
          typeof window.parseQuickAddProductFromButton === 'function'
            ? window.parseQuickAddProductFromButton(addToCartButton)
            : null;

        if (!productHandleValue) return;

        if (typeof window.openQuickAddModalProductCards === 'function') {
          window.openQuickAddModalProductCards(productHandleValue, productIdValue, prefetched, addToCartButton);
        } else {
          window.location.href = '/products/' + productHandleValue;
        }
        return;
      }

      const wishlistButton = event.target.closest(
        '.product-collection-showcase__card-wishlist',
      );
      const imageArrow = event.target.closest('.product-showcase-card-image-arrow');
      const colorSwatch = event.target.closest('.product-collection-showcase__card-color-swatch');
      const productCard = event.target.closest('.product-collection-showcase__card');

      if (wishlistButton || imageArrow) {
        return;
      }

      // Color swatch navigates to that product's PDP
      if (colorSwatch) {
        const swatchHandle = colorSwatch.getAttribute('data-product-handle');
        if (swatchHandle) {
          event.preventDefault();
          event.stopPropagation();
          window.location.href = `/products/${swatchHandle}`;
        }
        return;
      }

      // Card click navigates to PDP
      if (productCard) {
        const productHandle = productCard.getAttribute('data-product-handle');
        if (productHandle) {
          window.location.href = `/products/${productHandle}`;
        }
      }
    });
  }

  runInlineScripts(root) {
    if (!root) return;
    const scripts = root.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      // Copy attributes (except type="module" to avoid unexpected behavior)
      Array.from(oldScript.attributes).forEach((attr) => {
        if (attr.name === 'type' && attr.value === 'module') return;
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      document.body.appendChild(newScript);
    });
  }

  async loadRecommendations() {
    console.log('YMAL: Starting to load recommendations...');
    
    // If already loaded by Liquid on server-side, just init slider
    if (this.querySelector('.ymal-slider-loaded')) {
      console.log('YMAL: Content already present, initializing slider.');
      this.initSlider();
      return;
    }

    try {
      const response = await fetch(this.baseUrl);
      const html = await response.text();
      const div = document.createElement('div');
      div.innerHTML = html;
      
      const recommendations = div.querySelector('.ymal-slider-loaded');
      console.log('YMAL: API Response received. Found content:', !!recommendations);
      
      if (recommendations && recommendations.innerHTML.trim().length > 0) {
        const containerWrapper = div.querySelector('.you-may-also-like__container')
          ? div.querySelector('.you-may-also-like__container').parentElement
          : null;

        if (!containerWrapper) {
          console.warn('YMAL: Could not find you-may-also-like container in fetched HTML.');
          this.style.display = 'none';
          return;
        }

        // Replace content
        this.innerHTML = containerWrapper.innerHTML;
        this._additionalDataLoadStarted = false;

        // Ensure any inline scripts from snippets (e.g. product-showcase-card)
        // are executed so card JS behaves like in the original section.
        this.runInlineScripts(containerWrapper);

        this.initSlider();
        
        if (window.AOS) {
          window.AOS.refresh();
        }
      } else {
        console.log('YMAL: No recommendations found or empty content.');
        this.style.display = 'none';
      }
    } catch (error) {
      console.error('YMAL: Error loading recommendations:', error);
      this.style.display = 'none';
    }
  }

  initSlider() {
    const sliderContainer = this.querySelector('.swiper');

    // Always remove loading class after some time
    setTimeout(() => {
      this.classList.remove('is-loading');
      this.style.opacity = '1';
    }, 500);

    if (!sliderContainer || typeof Swiper === 'undefined') {
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper not found, waiting...');
        setTimeout(() => this.initSlider(), 1000);
      }
      return;
    }

    const loadAdditionalData = () => {
      if (this._additionalDataLoadStarted) return;
      this._additionalDataLoadStarted = true;
      if (typeof this.loadAllProductAdditionalData === 'function') {
        void this.loadAllProductAdditionalData();
      }
    };

    // Reuse a healthy swiper — destroy/recreate was recalculating slide widths on mobile.
    if (this.swiper && !this.swiper.destroyed && this.swiper.el === sliderContainer) {
      loadAdditionalData();
      window.requestAnimationFrame(() => {
        if (this.swiper && !this.swiper.destroyed) {
          this.swiper.update();
        }
      });
      return;
    }

    if (this.swiper) {
      this.swiper.destroy();
    }

    if (typeof window.initYouMayAlsoLikeMainSwiper === 'function') {
      window.initYouMayAlsoLikeMainSwiper(this);
      loadAdditionalData();
      return;
    }

    if (sliderContainer?.querySelector('.product-collection-showcase__card')) {
      loadAdditionalData();
    }

    setTimeout(() => this.initSlider(), 100);
  }

  /**
   * Load PDP review badge + custom_display_text into each product card,
   * same behavior as product-collection-showcase section.
   */
  async loadProductAdditionalData(productHandleValue) {
    try {
      const productDataUrl = `/products/${productHandleValue}?view=review`;
      const productDataResponse = await fetch(productDataUrl);
      const productDataHTML = await productDataResponse.text();
      const temporaryDiv = document.createElement('div');
      temporaryDiv.innerHTML = productDataHTML;

      const customDisplayTextElement = temporaryDiv.querySelector('.custom_display_text');
      const customTitleContainer = this.querySelector(
        `.product-collection-showcase__card[data-product-handle="${productHandleValue}"] .product-collection-showcase__card-custom-title`,
      );

      if (customDisplayTextElement && customTitleContainer) {
        const clonedTextElement = customDisplayTextElement.cloneNode(true);
        if (clonedTextElement.textContent) {
          if (typeof theme !== 'undefined' && theme.utils && theme.utils.toProperCase) {
            clonedTextElement.textContent = theme.utils.toProperCase(clonedTextElement.textContent);
          }
        }
        customTitleContainer.innerHTML = clonedTextElement.outerHTML;
      }

      const reviewBadgeElement = temporaryDiv.querySelector('.jdgm-preview-badge');
      const reviewContainerElement = this.querySelector(
        `.product-collection-showcase__card[data-product-handle="${productHandleValue}"] .product-collection-showcase__card-review`,
      );

      if (reviewBadgeElement && reviewContainerElement) {
        const previousBadgeElement = reviewBadgeElement.querySelector('.jdgm-prev-badge');
        const totalReviewCount = previousBadgeElement
          ? parseInt(previousBadgeElement.getAttribute('data-number-of-reviews') || '0', 10)
          : 0;

        if (totalReviewCount > 0) {
          reviewContainerElement.innerHTML = reviewBadgeElement.outerHTML;

          const reviewBadgeTextElement = reviewContainerElement.querySelector('.jdgm-prev-badge__text');
          if (reviewBadgeTextElement) {
            const reviewText = reviewBadgeTextElement.textContent.trim();
            const reviewCountMatch = reviewText.match(/(\d+)\s+review(s)?/i);
            if (reviewCountMatch) {
              const reviewNumber = reviewCountMatch[1];
              reviewBadgeTextElement.textContent = `(${reviewNumber})`;
            }
          }

          if (typeof window.jdgm !== 'undefined' && window.jdgm.loadWidgets) {
            window.jdgm.loadWidgets();
          }
        } else {
          const reviewWrapperElement = reviewContainerElement?.closest(
            '.product-collection-showcase__card-review-wrapper',
          );
          if (reviewWrapperElement) {
            reviewWrapperElement.style.display = 'none';
          }
        }
      }

      document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
    } catch (error) {
      // Fail silently; YMAL should still render even if PDP review/custom text fails.
    }
  }

  escapeHtmlAttribute(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  escapeHtmlUrl(value) {
    return String(value || '').replace(/"/g, '&quot;');
  }

  buildColorSwatchesHTML(colorOptions, currentHandle) {
    const colorCount = colorOptions.length;
    const showColorText = colorCount > 3;
    const displayedSwatches = Math.min(5, colorCount);
    const remaining = colorCount > displayedSwatches ? colorCount - displayedSwatches : 0;
    const onError =
      "onerror=\"if(this.dataset.fallback&&this.src!==this.dataset.fallback){this.src=this.dataset.fallback}else{this.style.display='none';this.parentElement.innerHTML='<div style=&quot;width:100%;height:100%;background-color:#f0f0f0&quot;></div>';}\"";

    const sortedColorOptions = [...colorOptions].sort((a, b) => {
      const aIsCurrent = a.handle === currentHandle;
      const bIsCurrent = b.handle === currentHandle;
      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;
      return 0;
    });

    let html = '';
    if (showColorText) {
      html += '<div class="product-collection-showcase__card-colors-count">3 Colors +</div>';
    }
    html += '<div class="product-collection-showcase__card-colors">';

    sortedColorOptions.slice(0, displayedSwatches).forEach((colorOption) => {
      const isCurrent = colorOption.handle === currentHandle;
      const colorImage = colorOption.image || colorOption.fallback;
      const colorFallback = colorOption.fallback || colorOption.image || '';
      const baseClass = 'product-collection-showcase__card-color-swatch';
      const currentClass = isCurrent ? ' product-collection-showcase__card-color-swatch--current' : '';

      html +=
        '<div class="' +
        baseClass +
        currentClass +
        '" data-color="' +
        this.escapeHtmlAttribute(colorOption.name) +
        '" title="' +
        this.escapeHtmlAttribute(colorOption.name) +
        '" data-product-handle="' +
        this.escapeHtmlAttribute(colorOption.handle) +
        '">';

      if (colorImage) {
        html +=
          '<img src="' +
          this.escapeHtmlUrl(colorImage) +
          '" alt="' +
          this.escapeHtmlAttribute(colorOption.name) +
          '" loading="lazy" data-fallback="' +
          this.escapeHtmlUrl(colorFallback) +
          '" ' +
          onError +
          '>';
      } else {
        html += '<div style="width:100%;height:100%;background-color:#f0f0f0"></div>';
      }

      html += '</div>';
    });

    if (remaining > 0) {
      html += '<div class="product-collection-showcase__card-colors-more">+' + remaining + '</div>';
    }

    html += '</div>';
    return html;
  }

  renderColorSwatchesIntoWrapper(wrapper, colorOptions, currentHandle) {
    if (!wrapper || !colorOptions?.length) return;

    const existingCount = wrapper.querySelector('.product-collection-showcase__card-colors-count');
    const existingColors = wrapper.querySelector('.product-collection-showcase__card-colors');
    if (existingCount) existingCount.remove();
    if (existingColors) existingColors.remove();

    wrapper.insertAdjacentHTML('beforeend', this.buildColorSwatchesHTML(colorOptions, currentHandle));
  }

  async fetchProductJson(productHandleValue) {
    try {
      const response = await fetch('/products/' + productHandleValue + '.js');
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async resolveColorTagForHandle(productHandleValue, wrapper) {
    const tagFromMarkup = wrapper?.getAttribute('data-color-tag');
    if (tagFromMarkup) return tagFromMarkup;

    const productJson = await this.fetchProductJson(productHandleValue);
    if (productJson) {
      return this.extractColorTagFromProduct(productJson);
    }

    try {
      const response = await fetch('/api/2024-07/graphql.json', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-shopify-storefront-access-token': '9f39520505c5a1a7f0bab22fb8a87abf',
        },
        body: JSON.stringify({
          query: `
            query getProductByHandle($handle: String!) {
              product(handle: $handle) {
                tags
              }
            }
          `,
          variables: { handle: productHandleValue },
        }),
      });
      const data = await response.json();
      const rawProduct = data?.data?.product;
      if (!rawProduct) return null;
      return this.extractColorTagFromProduct(this.flattenGraphQLConnections(rawProduct));
    } catch (error) {
      return null;
    }
  }

  /**
   * Load colorway swatches into the YMAL product card,
   * matching the markup used in product-collection-showcase.
   */
  async loadProductColorOptions(productHandleValue, wrapperOverride) {
    try {
      const card = this.querySelector(
        `.product-collection-showcase__card[data-product-handle="${productHandleValue}"]`,
      );
      if (!card) return;

      let wrapper =
        wrapperOverride ||
        card.querySelector('.product-collection-showcase__card-colors-wrapper[data-product-handle]') ||
        card.querySelector('.product-collection-showcase__card-colors-wrapper');

      const colorTag = await this.resolveColorTagForHandle(productHandleValue, wrapper);
      if (!colorTag) return;

      const colorOptions = await this.getProductColorOptionsFromColorTag(colorTag);
      if (!colorOptions?.length) return;

      if (!wrapper) {
        const priceEl = card.querySelector('.product-collection-showcase__card-price');
        if (!priceEl) return;
        wrapper = document.createElement('div');
        wrapper.className = 'product-collection-showcase__card-colors-wrapper';
        wrapper.setAttribute('data-product-handle', productHandleValue);
        wrapper.setAttribute('data-color-tag', colorTag);
        wrapper.innerHTML = '<div class="product-collection-showcase__card-colors"></div>';
        priceEl.insertAdjacentElement('afterend', wrapper);
      } else if (!wrapper.getAttribute('data-color-tag')) {
        wrapper.setAttribute('data-color-tag', colorTag);
      }

      this.renderColorSwatchesIntoWrapper(wrapper, colorOptions, productHandleValue);
    } catch (error) {
      // Swatches are non-critical; ignore errors.
    }
  }

  /**
   * Helper: load additional data for all cards in this YMAL instance.
   */
  async loadAllProductAdditionalData() {
    const cards = this.querySelectorAll('.product-collection-showcase__card[data-product-handle]');
    const handles = Array.from(cards)
      .map((card) => card.getAttribute('data-product-handle'))
      .filter(Boolean);

    const colorWrappers = this.querySelectorAll(
      '.product-collection-showcase__card-colors-wrapper[data-product-handle]',
    );
    const colorLoadsByTag = new Map();
    const handlesNeedingTagLookup = [];

    colorWrappers.forEach((wrapper) => {
      const handle = wrapper.getAttribute('data-product-handle');
      const tag = wrapper.getAttribute('data-color-tag');
      if (!handle) return;
      if (tag) {
        if (!colorLoadsByTag.has(tag)) colorLoadsByTag.set(tag, []);
        colorLoadsByTag.get(tag).push({ wrapper, handle });
      } else {
        handlesNeedingTagLookup.push({ wrapper, handle });
      }
    });

    if (handlesNeedingTagLookup.length) {
      await Promise.all(
        handlesNeedingTagLookup.map(async ({ wrapper, handle }) => {
          const tag = await this.resolveColorTagForHandle(handle, wrapper);
          if (!tag) return;
          wrapper.setAttribute('data-color-tag', tag);
          if (!colorLoadsByTag.has(tag)) colorLoadsByTag.set(tag, []);
          colorLoadsByTag.get(tag).push({ wrapper, handle });
        }),
      );
    }

    const colorLoadPromises = Array.from(colorLoadsByTag.entries()).map(async ([colorTag, items]) => {
      const colorOptions = await this.getProductColorOptionsFromColorTag(colorTag);
      if (!colorOptions?.length) return;
      items.forEach(({ wrapper, handle }) => {
        this.renderColorSwatchesIntoWrapper(wrapper, colorOptions, handle);
      });
    });

    await Promise.all([
      ...handles.map((handle) => this.loadProductAdditionalData(handle)),
      ...colorLoadPromises,
    ]);

    const wrappersWithoutMarkup = handles.filter((handle) => {
      return !this.querySelector(
        `.product-collection-showcase__card-colors-wrapper[data-product-handle="${handle}"] .product-collection-showcase__card-color-swatch`,
      );
    });

    if (wrappersWithoutMarkup.length) {
      await Promise.all(wrappersWithoutMarkup.map((handle) => this.loadProductColorOptions(handle)));
    }

    window.requestAnimationFrame(() => {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.update();
      }
    });
  }
  /**
   * Utility: flatten GraphQL connections (edges -> node) recursively.
   */
  flattenGraphQLConnections(graphQLObject) {
    if (!(graphQLObject && typeof graphQLObject === 'object') || Array.isArray(graphQLObject)) return graphQLObject;
    if ('edges' in graphQLObject && Array.isArray(graphQLObject.edges)) {
      return graphQLObject.edges.map((edgeItem) => this.flattenGraphQLConnections(edgeItem.node));
    }
    for (const propertyName in graphQLObject)
      graphQLObject[propertyName] = this.flattenGraphQLConnections(graphQLObject[propertyName]);
    return graphQLObject;
  }

  /**
   * Utility: best-effort image URL validation (same as product-collection-showcase).
   */
  async validateImageUrl(imageUrl) {
    if (!imageUrl) return false;
    const timeoutMs = 4000;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(imageUrl, { method: 'GET', signal: controller.signal, credentials: 'omit' });
      clearTimeout(timeout);
      if (res.status === 404 || !res.ok) return false;
      const ct = (res.headers.get('Content-Type') || '').toLowerCase();
      if (ct && !ct.startsWith('image/')) return false;
      return true;
    } catch (e) {
      // Fallback to Image() check
      return await new Promise((resolve) => {
        try {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = imageUrl;
          setTimeout(() => resolve(false), timeoutMs);
        } catch (err) {
          resolve(false);
        }
      });
    }
  }

  extractSwatchImageFromVariant(variantData) {
    if (!variantData) return null;
    let swatchImageValue = null;

    if (variantData.metafield) {
      if (variantData.metafield.value !== null && variantData.metafield.value !== undefined) {
        swatchImageValue = variantData.metafield.value;
      } else if (typeof variantData.metafield === 'string') {
        swatchImageValue = variantData.metafield;
      }
    } else if (variantData.metafields && variantData.metafields.swatch_image) {
      swatchImageValue = variantData.metafields.swatch_image;
    }

    if (swatchImageValue !== null && swatchImageValue !== undefined) {
      const trimmed = String(swatchImageValue).trim();
      if (
        trimmed &&
        trimmed.length > 0 &&
        trimmed !== 'null' &&
        trimmed !== 'undefined' &&
        (trimmed.startsWith('http://') || trimmed.startsWith('https://'))
      ) {
        return trimmed;
      }
    }
    return null;
  }

  isStandardVariant(variant) {
    if (!variant) return false;
    const titleDowncase = (variant.title || '').toLowerCase();
    if (titleDowncase.includes('standard')) return true;
    const options = variant.selectedOptions || [];
    for (let i = 0; i < options.length; i++) {
      if ((options[i].value || '').toLowerCase() === 'standard') return true;
    }
    return false;
  }

  getVariantSwatchOrder(variants) {
    if (!variants || !variants.length) return [];
    const standard = variants.find((v) => this.isStandardVariant(v));
    if (standard) {
      const rest = variants.filter((v) => v !== standard);
      return [standard, ...rest];
    }
    return [...variants];
  }

  async getFirstValidSwatchUrl(colorwayProductItem) {
    const variants = colorwayProductItem.variants || [];
    const ordered = this.getVariantSwatchOrder(variants);
    for (const variant of ordered) {
      const metafieldUrl = this.extractSwatchImageFromVariant(variant);
      if (metafieldUrl && (await this.validateImageUrl(metafieldUrl))) return metafieldUrl;
    }
    for (const variant of ordered) {
      if (variant?.image?.url && (await this.validateImageUrl(variant.image.url))) return variant.image.url;
    }
    return null;
  }

  extractColorTagFromProduct(productData) {
    if (!productData.tags || !Array.isArray(productData.tags)) return null;
    for (const productTag of productData.tags) {
      if (productTag.includes('_') && productTag !== 'new_arrival' && productTag !== 'Endless_Summer') {
        return productTag;
      }
    }
    return null;
  }

  async fetchColorwayProductsByTag(colorTag) {
    if (!this.__colorwayDataCache) {
      this.__colorwayDataCache = new Map();
    }
    if (this.__colorwayDataCache.has(colorTag)) {
      return this.__colorwayDataCache.get(colorTag);
    }

    try {
      const response = await fetch('/api/2024-07/graphql.json', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-shopify-storefront-access-token': '9f39520505c5a1a7f0bab22fb8a87abf',
        },
        body: JSON.stringify({
          query: `
            query getProductsByTag($query: String!) {
              colorways: products(query: $query, first: 50) {
                edges {
                  node {
                    handle
                    title
                    images(first: 2) {
                      edges {
                        node {
                          width
                          height
                          url(transform: {maxHeight: 100, maxWidth: 100})
                        }
                      }
                    }
                    variants(first: 250) {
                      edges {
                        node {
                          title
                          availableForSale
                          image {
                            url(transform: {maxHeight: 100, maxWidth: 100})
                            width
                            height
                          }
                          selectedOptions {
                            name
                            value
                          }
                          metafield(namespace: "custom", key: "swatch_image") {
                            value
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: { query: `tag:${colorTag}` },
        }),
      });

      const data = await response.json();
      const flattened = this.flattenGraphQLConnections(data?.data?.colorways || []);
      this.__colorwayDataCache.set(colorTag, flattened);
      return flattened;
    } catch (error) {
      return [];
    }
  }

  async getProductColorOptionsFromColorTag(colorTag) {
    try {
      const colorwayProductsList = await this.fetchColorwayProductsByTag(colorTag);
      if (!colorwayProductsList.length) return [];

      const uniqueColorOptionsMap = new Map();
      const colorwayProcessingPromises = colorwayProductsList.map(async (colorwayProductItem) => {
        let fallbackImageUrl = null;
        if (colorwayProductItem.variants?.length) {
          const v = colorwayProductItem.variants.find((x) => x.image?.url);
          fallbackImageUrl = v?.image?.url || colorwayProductItem.variants[0]?.image?.url;
        }
        if (!fallbackImageUrl && colorwayProductItem.images?.[0]?.url) {
          fallbackImageUrl = colorwayProductItem.images[0].url;
        }

        const swatchImageUrl = await this.getFirstValidSwatchUrl(colorwayProductItem);
        const finalImage = swatchImageUrl || fallbackImageUrl;
        const finalFallback = fallbackImageUrl || finalImage;

        return {
          name: colorwayProductItem.title,
          handle: colorwayProductItem.handle,
          image: finalImage,
          fallback: finalFallback,
          available: colorwayProductItem.variants?.some((variantItem) => variantItem.availableForSale) || false,
        };
      });

      const processedColorOptions = await Promise.all(colorwayProcessingPromises);

      processedColorOptions.forEach((colorOption) => {
        if (!uniqueColorOptionsMap.has(colorOption.handle)) {
          uniqueColorOptionsMap.set(colorOption.handle, colorOption);
        }
      });

      return Array.from(uniqueColorOptionsMap.values());
    } catch (error) {
      return [];
    }
  }
}

customElements.define('you-may-also-like', YouMayAlsoLike);
})();
